import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ConfirmModal from '../common/ConfirmModal';

const DriverAvailability = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';
  const [availability, setAvailability] = useState({
    Monday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Tuesday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Wednesday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Thursday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Friday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Saturday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] },
    Sunday: { is_available: false, all_day: false, time_ranges: [{ start_time: '09:00', end_time: '17:00' }] }
  });
  const [blackouts, setBlackouts] = useState([]);
  const [newBlackout, setNewBlackout] = useState({
    start_datetime: '',
    end_datetime: '',
    reason: '',
    all_day: false
  });
  
  // NEW: State for tracking changed days and showing success message
  const [daysChanged, setDaysChanged] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [deleteBlackoutConfirm, setDeleteBlackoutConfirm] = useState({ isOpen: false, id: null });

  useEffect(() => {
    fetchAvailability();
    fetchBlackouts();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/availability/days`);
      const availData = {};
      
      // Parse backend response - backend returns objects with start_time_1, start_time_2, start_time_3
      response.data.forEach(day => {
        const time_ranges = [];
        
        // Extract time ranges from numbered fields
        if (day.start_time_1 && day.end_time_1) {
          time_ranges.push({
            start_time: day.start_time_1,
            end_time: day.end_time_1
          });
        }
        if (day.start_time_2 && day.end_time_2) {
          time_ranges.push({
            start_time: day.start_time_2,
            end_time: day.end_time_2
          });
        }
        if (day.start_time_3 && day.end_time_3) {
          time_ranges.push({
            start_time: day.start_time_3,
            end_time: day.end_time_3
          });
        }
        
        availData[day.day_of_week] = {
          is_available: day.is_available,
          all_day: day.all_day || false,
          time_ranges: time_ranges.length > 0 ? time_ranges : [{ start_time: '09:00', end_time: '17:00' }]
        };
      });
      
      setAvailability(prev => ({ ...prev, ...availData }));
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchBlackouts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/drivers/availability/blackouts`);
      setBlackouts(response.data);
    } catch (error) {
      console.error('Error fetching blackouts:', error);
    }
  };

  const handleDayChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleAllDayToggle = (day, checked) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        all_day: checked,
        time_ranges: checked 
          ? [{ start_time: '00:00', end_time: '23:59' }]
          : [{ start_time: '09:00', end_time: '17:00' }]
      }
    }));
  };

  const handleTimeRangeChange = (day, index, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        time_ranges: prev[day].time_ranges.map((range, i) =>
          i === index ? { ...range, [field]: value } : range
        )
      }
    }));
  };

  // Helper function to convert HH:MM to minutes since midnight
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to convert minutes since midnight to HH:MM
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Helper function to add 1 minute to a time
  const addOneMinute = (timeStr) => {
    const minutes = timeToMinutes(timeStr);
    return minutesToTime(minutes + 1);
  };

  const addTimeRange = (day) => {
    setAvailability(prev => {
      const currentRanges = prev[day].time_ranges;
      
      // Find the latest end time from existing ranges
      let latestEndTime = '09:00';
      currentRanges.forEach(range => {
        if (timeToMinutes(range.end_time) > timeToMinutes(latestEndTime)) {
          latestEndTime = range.end_time;
        }
      });
      
      // New range starts 1 minute after the latest end time
      const newStartTime = addOneMinute(latestEndTime);
      
      // New range ends 4 hours later (or at 23:59 if that would exceed)
      const newStartMinutes = timeToMinutes(newStartTime);
      const fourHoursLater = newStartMinutes + (4 * 60);
      const maxMinutes = timeToMinutes('23:59');
      const newEndMinutes = Math.min(fourHoursLater, maxMinutes);
      const newEndTime = minutesToTime(newEndMinutes);
      
      return {
        ...prev,
        [day]: {
          ...prev[day],
          time_ranges: [...prev[day].time_ranges, { start_time: newStartTime, end_time: newEndTime }]
        }
      };
    });
  };

  const removeTimeRange = (day, index) => {
    setAvailability(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        time_ranges: prev[day].time_ranges.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSaveAvailability = async () => {
    try {
      const availabilityData = [];
      
      Object.entries(availability).forEach(([day, data]) => {
        // Create a single object for this day
        const dayData = {
          day_of_week: day,
          is_available: data.is_available,
          all_day: data.all_day || false,
          start_time_1: null,
          end_time_1: null,
          start_time_2: null,
          end_time_2: null,
          start_time_3: null,
          end_time_3: null
        };
        
        if (data.is_available) {
          if (data.all_day) {
            // Single all-day entry
            dayData.start_time_1 = '00:00';
            dayData.end_time_1 = '23:59';
          } else {
            // Sort time ranges by start time to ensure correct ordering
            const sortedRanges = [...data.time_ranges].sort((a, b) => 
              timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
            );
            
            // Map time ranges to numbered fields
            sortedRanges.forEach((range, index) => {
              if (index === 0) {
                dayData.start_time_1 = range.start_time;
                dayData.end_time_1 = range.end_time;
              } else if (index === 1) {
                dayData.start_time_2 = range.start_time;
                dayData.end_time_2 = range.end_time;
              } else if (index === 2) {
                dayData.start_time_3 = range.start_time;
                dayData.end_time_3 = range.end_time;
              }
            });
          }
        }
        
        availabilityData.push(dayData);
      });

      // UPDATED: POST to backend and capture response with days_changed
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/availability/days`,
        availabilityData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Handle response - show modal for changed days
      if (response.data.days_changed && response.data.days_changed.length > 0) {
        // Days were changed - show modal with pricing reminder
        setDaysChanged(response.data.days_changed);
        setShowSuccessMessage(true);
        // Don't auto-navigate - let user choose via modal buttons
      } else {
        // No days changed but submission successful
        alert('Availability submitted successfully! No changes were detected.');
        // If in onboarding, go back to onboarding checklist
        if (isOnboarding) {
          navigate('/driver/onboarding');
        }
      }

    } catch (error) {
      console.error('Error updating availability:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update availability. Please try again.';
      alert(errorMessage);
    }
  };

  const handleBlackoutAllDayToggle = (checked) => {
    if (checked) {
      // When switching to all-day, convert current date to just date part
      const currentDate = newBlackout.start_datetime ? 
        newBlackout.start_datetime.split('T')[0] : 
        new Date().toISOString().split('T')[0];
      
      setNewBlackout({
        ...newBlackout,
        all_day: true,
        start_datetime: currentDate,
        end_datetime: currentDate
      });
    } else {
      // When switching to specific times, add default time
      const currentDate = newBlackout.start_datetime || new Date().toISOString().split('T')[0];
      setNewBlackout({
        ...newBlackout,
        all_day: false,
        start_datetime: `${currentDate}T09:00`,
        end_datetime: `${currentDate}T17:00`
      });
    }
  };

  const handleBlackoutDateChange = (field, value) => {
    if (newBlackout.all_day) {
      // For all-day blackouts, set both start and end to the same date
      setNewBlackout({
        ...newBlackout,
        start_datetime: value,
        end_datetime: value
      });
    } else {
      setNewBlackout({
        ...newBlackout,
        [field]: value
      });
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();
    try {
      let startDatetime, endDatetime;
      
      if (newBlackout.all_day) {
        // For all-day: set to midnight start and 11:59 PM end
        startDatetime = `${newBlackout.start_datetime}T00:00:00`;
        endDatetime = `${newBlackout.end_datetime}T23:59:00`;
      } else {
        startDatetime = newBlackout.start_datetime;
        endDatetime = newBlackout.end_datetime;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/availability/blackouts`,
        {
          start_datetime: startDatetime,
          end_datetime: endDatetime,
          reason: newBlackout.reason,
          all_day: newBlackout.all_day
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      setNewBlackout({ start_datetime: '', end_datetime: '', reason: '', all_day: false });
      fetchBlackouts();
      alert('Blackout period added successfully');
    } catch (error) {
      console.error('Error adding blackout:', error);
      alert('Failed to add blackout period');
    }
  };

  const handleDeleteBlackout = async (blackoutId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/drivers/availability/blackouts/${blackoutId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      fetchBlackouts();
      alert('Blackout period deleted successfully');
    } catch (error) {
      console.error('Error deleting blackout:', error);
      alert('Failed to delete blackout period');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(isOnboarding ? '/driver/onboarding' : -1)}
              className="text-gray-600 hover:text-gray-900"
            >
              ← {isOnboarding ? 'Back to Onboarding' : 'Back'}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Manage Availability</h1>
          </div>
        </div>
      </header>

      {/* Success Modal Popup */}
      {showSuccessMessage && daysChanged.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Availability Updated!
              </h3>
              <p className="text-gray-600">
                Your availability for{' '}
                <span className="font-semibold">
                  {daysChanged.length === 1
                    ? daysChanged[0]
                    : daysChanged.length === 2
                    ? `${daysChanged[0]} and ${daysChanged[1]}`
                    : `${daysChanged.slice(0, -1).join(', ')}, and ${daysChanged[daysChanged.length - 1]}`}
                </span>
                {' '}has been saved successfully.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> Please ensure all of your availability is priced, otherwise riders will not be able to book rides during unpriced times.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowSuccessMessage(false);
                  navigate(isOnboarding ? '/driver/onboarding' : -1);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {isOnboarding ? 'Back to Onboarding' : 'Done'}
              </button>
              <button
                onClick={() => {
                  setShowSuccessMessage(false);
                  // Navigate to pricing with onboarding param if in onboarding flow
                  const pricingUrl = isOnboarding ? '/driver/pricing?onboarding=true' : '/driver/pricing';
                  navigate(pricingUrl, { state: { requiredDays: daysChanged } });
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Go to Pricing (Recommended)
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Weekly Schedule */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Weekly Schedule</h2>
          <div className="space-y-6">
            {Object.entries(availability).map(([day, data]) => (
              <div key={day} className="p-4 border rounded-lg">
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
                  <div className="w-28 sm:w-32">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={data.is_available}
                        onChange={(e) => handleDayChange(day, 'is_available', e.target.checked)}
                        className="mr-2 h-4 w-4"
                      />
                      <span className="font-semibold">{day}</span>
                    </label>
                  </div>

                  {data.is_available && (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={data.all_day}
                        onChange={(e) => handleAllDayToggle(day, e.target.checked)}
                        className="mr-2 h-4 w-4"
                      />
                      <span className="text-sm text-gray-600">All day</span>
                    </label>
                  )}
                </div>

                {data.is_available && !data.all_day && (
                  <div className="space-y-2 mt-2 sm:ml-32">
                    {data.time_ranges.map((range, index) => (
                      <div key={index} className="flex flex-wrap items-center gap-2">
                        <input
                          type="time"
                          value={range.start_time}
                          onChange={(e) => handleTimeRangeChange(day, index, 'start_time', e.target.value)}
                          className="px-2 sm:px-3 py-2 border rounded text-sm sm:text-base"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={range.end_time}
                          onChange={(e) => handleTimeRangeChange(day, index, 'end_time', e.target.value)}
                          className="px-2 sm:px-3 py-2 border rounded text-sm sm:text-base"
                        />
                        {data.time_ranges.length > 1 && (
                          <button
                            onClick={() => removeTimeRange(day, index)}
                            className="px-2 sm:px-3 py-2 text-red-600 hover:bg-red-50 rounded text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {data.time_ranges.length < 3 && (
                      <button
                        onClick={() => addTimeRange(day)}
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm"
                      >
                        + Add Time Range
                      </button>
                    )}
                    {data.time_ranges.length > 0 && (
                      <div className="text-xs text-gray-500 mt-2">
                        💡 New time ranges automatically start after your last window
                      </div>
                    )}
                  </div>
                )}

                {data.is_available && data.all_day && (
                  <div className="mt-2 sm:ml-32 text-sm text-gray-500">
                    Available 12:00 AM - 11:59 PM
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={handleSaveAvailability}
            className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
          >
            Save Schedule
          </button>
        </div>

        {/* Blackout Periods */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Blackout Periods</h2>
          
          <form onSubmit={handleAddBlackout} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3">Add New Blackout Period</h3>
            
            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newBlackout.all_day}
                  onChange={(e) => handleBlackoutAllDayToggle(e.target.checked)}
                  className="mr-2 h-4 w-4"
                />
                <span className="text-sm text-gray-600">All day</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {newBlackout.all_day ? 'Date' : 'Start Date & Time'}
                </label>
                <input
                  type={newBlackout.all_day ? 'date' : 'datetime-local'}
                  value={newBlackout.all_day ? newBlackout.start_datetime.split('T')[0] : newBlackout.start_datetime}
                  onChange={(e) => handleBlackoutDateChange('start_datetime', e.target.value)}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              {!newBlackout.all_day && (
                <div>
                  <label className="block text-sm font-medium mb-1">End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={newBlackout.end_datetime}
                    onChange={(e) => handleBlackoutDateChange('end_datetime', e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
              )}
            </div>

            {newBlackout.all_day && (
              <div className="mb-3 text-sm text-gray-500">
                Blackout will apply from 12:00 AM to 11:59 PM on selected date
              </div>
            )}

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Reason (Optional)</label>
              <input
                type="text"
                value={newBlackout.reason}
                onChange={(e) => setNewBlackout({ ...newBlackout, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder="e.g., Vacation, Personal appointment"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700"
            >
              Add Blackout Period
            </button>
          </form>

          {/* Existing Blackouts */}
          <div className="space-y-2">
            {blackouts.map(blackout => (
              <div key={blackout.id} className="flex items-center justify-between p-3 border rounded">
                <div>
                  <div className="font-medium">
                    {new Date(blackout.start_datetime).toLocaleString()} - {new Date(blackout.end_datetime).toLocaleString()}
                  </div>
                  {blackout.reason && <div className="text-sm text-gray-600">{blackout.reason}</div>}
                  {blackout.all_day && <div className="text-xs text-gray-500 italic">All day</div>}
                </div>
                <button
                  onClick={() => setDeleteBlackoutConfirm({ isOpen: true, id: blackout.id })}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                >
                  Delete
                </button>
              </div>
            ))}
            {blackouts.length === 0 && (
              <p className="text-gray-500 text-center py-4">No blackout periods set</p>
            )}
          </div>
        </div>
      </main>

      <ConfirmModal
        isOpen={deleteBlackoutConfirm.isOpen}
        onClose={() => setDeleteBlackoutConfirm({ isOpen: false, id: null })}
        onConfirm={() => handleDeleteBlackout(deleteBlackoutConfirm.id)}
        title="Delete Blackout Period"
        message="Are you sure you want to delete this blackout period?"
        confirmText="Delete"
        confirmColor="red"
      />
    </div>
  );
};

export default DriverAvailability;